/* eslint-disable func-style */
import { Type, ɵstringify as stringify } from '@angular/core';
import { UTCDate } from '@date-fns/utc';
import { TimeFormatType } from 'app/main/schools/model/school.model';
import {
    endOfMonth,
    format,
    isAfter,
    isBefore,
    isSameDay,
    parse,
    startOfMonth,
    startOfToday,
    subDays,
    subMinutes,
    subMonths,
} from 'date-fns';
import { environment } from 'environments/environment';

const backDate = numOfDays => {
    const t = new Date();
    return new Date(t.setDate(t.getDate() - numOfDays));
};

export function getOffset(timeZone = 'UTC', date = new Date()) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
    return (tzDate.getTime() - utcDate.getTime()) / 6e4;
}

export function today(): Date {
    return startOfToday();
}

export function subtractDate(date: Date, day: number): Date {
    return subDays(date, day);
}

export function createDate(year: number, month: number, date: number): Date {
    return new UTCDate(new Date(year, month, date));
}

export const convertTime12to24 = (time12h: string, format: TimeFormatType = TimeFormatType.HOUR24) => {
    if (!time12h) {
        return '';
    }
    // console.log('TIME', time12h);
    const [time, modifier] = time12h.split(' ');

    let [hours, minutes] = time.split(':');

    if (format == TimeFormatType.HOUR12) {
        return `${
            parseInt(hours, 10) >= 13 ? parseInt(hours, 10) - 12 : parseInt(hours, 10) == 0 ? '12' : parseInt(hours, 10)
        }:${parseInt(minutes, 10) < 10 ? '0' : ''}${parseInt(minutes, 10)}${parseInt(hours, 10) > 11 ? ' PM' : ' AM'}`;
        // return `${hours}:${minutes} ${modifier}`;
    }
    if (hours == '12' && modifier == 'AM') {
        hours = '00';
    }

    if (modifier == 'PM') {
        hours = `${parseInt(hours, 10) + 12}`;
    }
    return `${hours}:${minutes}`;
};

export function isGreater(first: Date, last: Date) {
    return isAfter(first, last);
}

export function isGreaterEqual(first: Date, last: Date) {
    return isAfter(first, last) || isSameDay(first, last);
}

export function isLess(first: Date, last: Date) {
    return isBefore(first, last);
}

export function isLessEqual(first: Date, last: Date) {
    return isBefore(first, last) || isSameDay(first, last);
}

export function isEqual(first: Date, last: Date) {
    return isSameDay(first, last);
}

export function formatTime(time: string[]) {
    if (!time || time.length < 2) {
        return '';
    }
    let hoursNum = parseInt(time[0]);
    const minutesNum = parseInt(time[1]);
    const ampm = hoursNum >= 12 ? 'PM' : 'AM';
    hoursNum = hoursNum % 12;
    hoursNum = hoursNum ? hoursNum : 12;
    const minutes = minutesNum < 10 ? `0${minutesNum}` : minutesNum;
    const hours = hoursNum < 10 ? `0${hoursNum}` : hoursNum;
    return `${hours}:${minutes} ${ampm}`;
}

export const dateRange = {
    kk: {
        presets: [
            {
                presetLabel: 'Кеше',
                range: { startDate: backDate(1), endDate: today() },
            },
            {
                presetLabel: 'Соңғы 7 күн',
                range: { startDate: backDate(7), endDate: today() },
            },
            {
                presetLabel: 'Соңғы 30 күн',
                range: { startDate: backDate(30), endDate: today() },
            },
            {
                presetLabel: 'Осы ай',
                range: {
                    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
                },
            },
            {
                presetLabel: 'Өткен ай',
                range: {
                    startDate: startOfMonth(subMonths(new Date(), 1)),
                    endDate: endOfMonth(subMonths(new Date(), 1)),
                },
            },
        ],
        format: 'dd.MM.yyyy',
        range: { startDate: null, endDate: null },
        applyLabel: 'Қолдану',
        calendarOverlayConfig: {
            shouldCloseOnBackdropClick: false,
            hasBackDrop: false,
        },
    },
    en: {
        presets: [
            {
                presetLabel: 'Yesterday',
                range: { startDate: backDate(1), endDate: today() },
            },
            {
                presetLabel: 'Last 7 Days',
                range: { startDate: backDate(7), endDate: today() },
            },
            {
                presetLabel: 'Last 30 Days',
                range: { startDate: backDate(30), endDate: today() },
            },
            {
                presetLabel: 'This Month',
                range: {
                    startDate: startOfMonth(new Date()),
                    endDate: endOfMonth(new Date()),
                },
            },
            {
                presetLabel: 'Last Month',
                range: {
                    startDate: startOfMonth(subMonths(new Date(), 1)),
                    endDate: endOfMonth(subMonths(new Date(), 1)),
                },
            },
        ],
        format: 'MM/dd/yyyy',
        range: { startDate: null, endDate: null },
        applyLabel: 'Apply',
        calendarOverlayConfig: {
            shouldCloseOnBackdropClick: false,
            hasBackDrop: false,
        },
    },
    tr: {
        presets: [
            {
                presetLabel: 'Dün',
                range: { startDate: backDate(1), endDate: today() },
            },
            {
                presetLabel: 'Son 7 gün',
                range: { startDate: backDate(7), endDate: today() },
            },
            {
                presetLabel: 'Son 30 gün',
                range: { startDate: backDate(30), endDate: today() },
            },
            {
                presetLabel: 'Bu ay',
                range: {
                    startDate: startOfMonth(new Date()),
                    endDate: endOfMonth(new Date()),
                },
            },
            {
                presetLabel: 'Son ay',
                range: {
                    startDate: startOfMonth(subMonths(new Date(), 1)),
                    endDate: endOfMonth(subMonths(new Date(), 1)),
                },
            },
        ],
        format: 'dd/MM/yyyy',
        range: { startDate: null, endDate: null },
        applyLabel: 'Uygula',
        calendarOverlayConfig: {
            shouldCloseOnBackdropClick: false,
            hasBackDrop: false,
        },
    },
    de: {
        presets: [
            {
                presetLabel: 'Yesterday',
                range: { startDate: backDate(1), endDate: today() },
            },
            {
                presetLabel: 'Last 7 Days',
                range: { startDate: backDate(7), endDate: today() },
            },
            {
                presetLabel: 'Last 30 Days',
                range: { startDate: backDate(30), endDate: today() },
            },
            {
                presetLabel: 'This Month',
                range: {
                    startDate: startOfMonth(new Date()),
                    endDate: endOfMonth(new Date()),
                },
            },
            {
                presetLabel: 'Last Month',
                range: {
                    startDate: startOfMonth(subMonths(new Date(), 1)),
                    endDate: endOfMonth(subMonths(new Date(), 1)),
                },
            },
        ],
        format: 'dd/MM/yyyy',
        range: { startDate: null, endDate: null },
        applyLabel: 'Apply',
        calendarOverlayConfig: {
            shouldCloseOnBackdropClick: false,
            hasBackDrop: false,
        },
    },
    ru: {
        presets: [
            {
                presetLabel: 'Вчера',
                range: { startDate: backDate(1), endDate: today() },
            },
            {
                presetLabel: 'Посл. 7 дней',
                range: { startDate: backDate(7), endDate: today() },
            },
            {
                presetLabel: 'Посл. 30 дней',
                range: { startDate: backDate(30), endDate: today() },
            },
            {
                presetLabel: 'Этот месяц',
                range: {
                    startDate: startOfMonth(new Date()),
                    endDate: endOfMonth(new Date()),
                },
            },
            {
                presetLabel: 'Посл. месяц',
                range: {
                    startDate: startOfMonth(subMonths(new Date(), 1)),
                    endDate: endOfMonth(subMonths(new Date(), 1)),
                },
            },
        ],
        format: 'dd.MM.yyyy',
        range: { startDate: null, endDate: null },
        applyLabel: 'Применить',
        calendarOverlayConfig: {
            shouldCloseOnBackdropClick: false,
            hasBackDrop: false,
        },
    },
};

export function isEmpty(value: any): boolean {
    return value == null || value == '' || value != value;
}

export function invalidPipeArgumentError(type: Type<any>, value: object) {
    return Error(`InvalidPipeArgument: '${value}' for pipe '${stringify(type)}'`);
}

export function strToNumber(value: number | string): number {
    // Convert strings to numbers
    if (typeof value == 'string' && !isNaN(Number(value) - parseFloat(value))) {
        return Number(value);
    }
    if (typeof value != 'number') {
        throw new Error(`${value} is not a number`);
    }
    return value;
}

export function splitDateTimeForField(instance: { [x: string]: any }, field: string) {
    const dateTime = field.concat('DateTime');
    if (instance && instance[dateTime]) {
        //const sourceOffset = new Date().getTimezoneOffset();
        let _date: Date = instance[dateTime];
        //_date = subMinutes(_date, zoneOffset);
        //_date = addMinutes(_date, zoneOffset);
        instance[field.concat('Date')] = _date;
        instance[field.concat('Time')] = format(_date, 'HH:mm');
    }
}

export function combineDateTimeForField(instance: { [x: string]: any }, enabled: boolean, field: string) {
    if (enabled && instance[field.concat('Date')]) {
        const _fromDate: Date = instance[field.concat('Date')];
        const _fromTime: Date = instance[field.concat('Time')]
            ? parse(instance[field.concat('Time')], 'HH:mm', _fromDate)
            : _fromDate;
        instance[field.concat('DateTime')] = subMinutes(_fromTime, environment.timezoneOffset);
        console.log('DATETIMEFIELD: ', field, instance[field.concat('DateTime')]);
    } else {
        instance[field.concat('Date')] = null;
        instance[field.concat('Time')] = null;
        instance[field.concat('DateTime')] = null;
    }
}
